// ============================================================
// Own-location match — never track/block the org's own stores
// ============================================================
// The shared DispensaryMap draws the org's OWN dispensaries twice: once as a
// purple "your location" marker AND once as a green, trackable competitor pin
// (the dispensary GeoJSON layer independently includes the same physical
// store). This helper decides whether a dispensary/competitor pin IS one of the
// org's own locations so the map can suppress the track/block action on it.
//
// Matching is by COORDINATE PROXIMITY, not name or id:
//   - the `locations` table has no google_place_id (no id join), and
//   - the own-location name ("Culture Cannabis Club  - Jurupa") differs
//     substantially from the dispensary's full legal name (no name join).
// So a pin is treated as an own store when its point sits within a small
// threshold of any org location's point. Kept pure + unit-testable here so the
// threshold and the match rule live in exactly one place.

/** A dispensary/competitor pin counts as an own store when its point is within
 *  this many meters of any org location. ~75 m absorbs same-building geocode
 *  drift (rooftop vs. parcel vs. street centroid) without swallowing a genuine
 *  neighboring dispensary. */
export const OWN_LOCATION_MATCH_METERS = 75

/** Minimal shape read from the `locations` prop — lat/lng may be number,
 *  string, or null. */
export interface OwnLocationPoint {
  lat?: number | string | null
  lng?: number | string | null
}

/** Coerce a coordinate that may arrive as number | string | null to a finite
 *  number, or null when absent/unparseable. */
function toNum(v: number | string | null | undefined): number | null {
  if (v == null) return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/** Great-circle distance between two [lng,lat] points, in meters (haversine).
 *  Exact enough at street scale; the equirectangular approximation would also
 *  do, but haversine avoids any latitude-scaling foot-guns. */
export function haversineMeters(
  aLng: number,
  aLat: number,
  bLng: number,
  bLat: number,
): number {
  const R = 6371000 // mean Earth radius, meters
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(bLat - aLat)
  const dLng = toRad(bLng - aLng)
  const lat1 = toRad(aLat)
  const lat2 = toRad(bLat)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** True when the pin at [lng,lat] is within `thresholdMeters` of ANY org
 *  location — i.e. the pin represents one of the operator's own stores.
 *  Null / NaN coordinates on either side are skipped and never match, so a
 *  location with no geocode can't accidentally suppress every pin. */
export function isOwnLocation(
  lng: number | null | undefined,
  lat: number | null | undefined,
  locations: OwnLocationPoint[] | undefined | null,
  thresholdMeters: number = OWN_LOCATION_MATCH_METERS,
): boolean {
  const pinLng = toNum(lng)
  const pinLat = toNum(lat)
  if (pinLng == null || pinLat == null) return false
  if (!locations?.length) return false
  for (const loc of locations) {
    const locLng = toNum(loc.lng)
    const locLat = toNum(loc.lat)
    if (locLng == null || locLat == null) continue
    if (haversineMeters(pinLng, pinLat, locLng, locLat) <= thresholdMeters) {
      return true
    }
  }
  return false
}

/** Shared eligibility gate for rival lists and map actions. The server flag
 * also covers delivery listings away from the owned storefront's coordinates.
 * Coordinate fallback checks every owned location, not just the selected one.
 */
export function isOwnedRivalCandidate(
  isOwn: boolean | undefined,
  lng: number | null | undefined,
  lat: number | null | undefined,
  locations: OwnLocationPoint[] | undefined | null,
): boolean {
  return isOwn === true || isOwnLocation(lng, lat, locations)
}
